'use client';

import './video.scss';

import dynamic from 'next/dynamic';
import { Suspense, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { VideoObject } from 'schema-dts';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { VideoProps } from '@sola/schema/lib/schema.ts';
import { trackClick } from '@sola/tracking/components/datalayer.tsx';
import { cookieConsentVideoURL } from '@sola/tracking/lib/video.ts';
import { getVideoType } from '@sola/utils/video.ts';

import { buildVideoJsonLd } from '../../utils/schema-utils.ts';
import { Image } from '../image/image.tsx';
import { JsonLd } from '../jsonLd/jsonLd.tsx';

const ReactPlayer = dynamic(() => import('react-player'), { ssr: false });

const VideoSkeleton = ({
  ratio,
  isPortrait,
}: {
  ratio: string;
  isPortrait: boolean;
}) => (
  <div
    className="Video__skeleton"
    style={{
      aspectRatio: ratio || '16/9',
      width: isPortrait ? 'auto' : '100%',
      height: isPortrait ? '100%' : 'auto',
    }}
  >
    <Skeleton width="100%" height="100%" />
  </div>
);

const videoOptions = {
  default: {
    controls: true,
    config: { vimeo: { dnt: true } },
  },
  background: {
    loop: true,
    muted: true,
    autoPlay: true,
    controls: false,
    playsInline: true,
    config: {
      youtube: {
        modestbranding: 1 as const,
        showinfo: 0 as const,
        rel: 0 as const,
      },
      vimeo: {
        background: true,
        dnt: true,
        transparent: false,
        playsinline: true,
        muted: true,
      },
    },
  },
} as const;

const getPlayerWidth = (isBackground: boolean, isPortrait: boolean) => {
  if (isBackground) {
    return 'inherit';
  }
  if (isPortrait) {
    return 'auto';
  }
  return '100%';
};

export const Video = ({
  url,
  poster,
  isBackground,
  autoplay = false,
  allowPlaying = true,
  width,
  height,
  thumbnailUrl,
  title,
  uploadDate,
}: VideoProps) => {
  const { t } = useTranslation(['skippy']);
  const [hasStarted, setHasStarted] = useState(false);
  const [playing, setPlaying] = useState(
    allowPlaying && (!!isBackground || autoplay),
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const playerRef = useRef<any>(null);

  const videoType = getVideoType(url);
  const variableVideoUrl = cookieConsentVideoURL(url);
  const isPortrait = width && height && height > width;
  const shouldRenderSchema = uploadDate && (poster?.url || thumbnailUrl);
  const aspectRatioStr = width && height ? `${width} / ${height}` : '16/9';
  const playerWidth = getPlayerWidth(!!isBackground, !!isPortrait);

  const handlePlayPauseToggle = () => {
    const label = playing ? t('skippy:video.pause') : t('skippy:video.play');
    trackClick(label);

    const next = !playing;
    setPlaying(next);

    const ip = playerRef.current?.getInternalPlayer?.();
    if (next) {
      ip?.play?.();
    } else {
      ip?.pause?.();
    }
  };

  const handleVideoStart = () => {
    if (!hasStarted) {
      const timeout = setTimeout(() => setHasStarted(true), 100);
      return () => clearTimeout(timeout);
    }
  };

  return (
    <div
      className={cn(
        `Video Video--${videoType}`,
        isBackground ? 'Video--background' : 'Video--inline',
        isPortrait ? 'h-full' : 'w-full',
      )}
    >
      {shouldRenderSchema && (
        <JsonLd<VideoObject>
          item={{
            '@context': 'https://schema.org',
            '@type': 'VideoObject',
            ...buildVideoJsonLd({
              name: title || 'Video',
              thumbnailUrl: poster?.url || thumbnailUrl,
              contentUrl: variableVideoUrl,
              embedUrl: url,
              description: title,
              uploadDate,
            }),
          }}
        />
      )}

      <Suspense
        fallback={
          <VideoSkeleton ratio={aspectRatioStr} isPortrait={!!isPortrait} />
        }
      >
        <ReactPlayer
          ref={playerRef}
          src={variableVideoUrl}
          playing={playing && allowPlaying}
          wrapper="div"
          className={cn('Video__player', {
            'Video__player--portrait': isPortrait,
            'Video__player--landscape': !isPortrait && width,
            'Video__player--default': !width && !height,
          })}
          width={playerWidth}
          height={isBackground || isPortrait ? 'inherit' : 'auto'}
          onPlay={() => {
            setPlaying(true);
            handleVideoStart();
          }}
          onStart={handleVideoStart}
          onProgress={handleVideoStart}
          {...videoOptions[isBackground ? 'background' : 'default']}
          style={{
            aspectRatio:
              !isBackground && width && height ? aspectRatioStr : undefined,
          }}
        />
      </Suspense>

      {poster && isBackground && (
        <Image
          className={cn('Video__poster', {
            'Video__poster--hidden': hasStarted,
          })}
          image={poster}
          priority
        />
      )}

      {!isBackground && !hasStarted && autoplay && (
        <div className="Video__loading-overlay">
          <VideoSkeleton ratio={aspectRatioStr} isPortrait={!!isPortrait} />
        </div>
      )}

      {isBackground && hasStarted && (
        <div className="Video__controls absolute bottom-0 right-0 p-comfy-sm">
          <ButtonStyle variant="tertiary" size="md">
            <button type="button" onClick={handlePlayPauseToggle}>
              <ArkIcon
                iconName={playing ? 'pause-circle' : 'play-circle'}
                size="20"
              />
              {playing ? t('skippy:video.pause') : t('skippy:video.play')}
            </button>
          </ButtonStyle>
        </div>
      )}
    </div>
  );
};
